import { Config } from 'config/envConfig';
import React, { useState, useEffect, useRef } from 'react';
import { BatchMode, BatchType, ModelTiers } from '@quyva/common';
import axios from 'axios';
import { Button, DragDropFile, NoInteractionView, Dropdown, alert as showAlert, AlertType } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faFile,
    faFileUpload,
    faPlus,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { isMobile } from 'react-device-detect';
import { getUrl } from './pdfDisplay/utils';
import { useGlobalCredits } from '../context/GlobalStateContext';

// ── Model multi-select component ───────────────────────────────────────────
const ModelMultiSelect = ({ selectedModels, onChange, availableCustomModels }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const ref = useRef(null);

    const allModels = [
        { category: 'Default Models', names: [ModelTiers.STANDARD, ModelTiers.PRECISION] },
        ...Object.entries(availableCustomModels || {}).map(([cat, names]) => ({ category: cat, names })),
    ];

    const addModel = (name) => {
        if (!selectedModels.includes(name)) {
            onChange([...selectedModels, name]);
        }
        setDropdownOpen(false);
    };

    const removeModel = (name) => {
        onChange(selectedModels.filter((m) => m !== name));
    };

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setDropdownOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className='flex flex-col gap-2'>
            <div className='text-sm font-medium text-texts-secondary300'>Select models</div>
            <div className='flex flex-wrap gap-2 items-center' ref={ref}>
                {selectedModels.map((m) => (
                    <span
                        key={m}
                        className='flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-backgrounds-primary100 text-texts-tertiary400 border border-backgrounds-primary200'
                    >
                        <span className='w-2 h-2 rounded-full bg-backgrounds-primary300 inline-block' />
                        {m}
                        <Button
                            icon={faTimes}
                            onClick={() => removeModel(m)}
                            type='danger'
                            className='ml-1 !w-5 !h-5 !border-0 !shadow-none'
                        />
                    </span>
                ))}

                <div className='relative'>
                    <Button
                        text='Add model'
                        icon={faPlus}
                        onClick={() => setDropdownOpen((o) => !o)}
                        hoverText='Add a model'
                        className='text-xs !py-1 !px-2 !font-normal'
                    />

                    {dropdownOpen && (
                        <div className='absolute z-50 top-full left-0 mt-1 w-56 bg-backgrounds-white rounded-lg shadow-lg border border-backgrounds-border py-1 max-h-60 overflow-y-auto'>
                            {allModels.map(({ category, names }) => (
                                <div key={category}>
                                    <div className='px-3 py-1 text-xs font-semibold text-texts-secondary300 uppercase tracking-wider'>
                                        {category}
                                    </div>
                                    {names.map((name) => {
                                        const already = selectedModels.includes(name);
                                        return (
                                            <Button
                                                key={name}
                                                text={already ? `${name} ✓` : name}
                                                disabled={already}
                                                onClick={() => addModel(name)}
                                                className={`w-full !justify-start !text-xs !font-normal px-4 !border-0 !shadow-none !rounded-none !py-1 ${already ? 'opacity-40' : ''}`}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────
const BatchAndEval = ({ batchType, repo_to_chat }) => {
    const { availableCustomModels } = useGlobalCredits();
    const containerRef = useRef(null);

    // ── Eval toggle (false = Batch, true = Eval) ──────────────────────────
    const [evalMode, setEvalMode] = useState(false);

    // ── Shared state ──────────────────────────────────────────────────────
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedModels, setSelectedModels] = useState(['Standard']);

    // ── Virtual repo (QnA) ────────────────────────────────────────────────
    const [virtualRepos, setVirtualRepos] = useState([]);
    const [selectedVirtualRepoId, setSelectedVirtualRepoId] = useState(null);

    // ── Extraction state ──────────────────────────────────────────────────
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [appendTemplateConflict, setAppendTemplateConflict] = useState('');

    // Reset file/error when toggle or batchType changes
    useEffect(() => {
        setSelectedFile(null);
        setError('');
        setAppendTemplateConflict('');
    }, [evalMode, batchType]);

    // ── Fetch virtual repos (QnA only) ────────────────────────────────────
    useEffect(() => {
        if (batchType !== BatchType.QNA || !repo_to_chat?.repoid) return;
        const fetchVirtualRepos = async () => {
            try {
                const url = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/virtual_repo_routes/virtual-repos`
                );
                const response = await axios.get(url, {
                    params: { parent_repo_id: repo_to_chat.repoid },
                });
                const list = response?.data?.virtual_repos || [];
                const defaultId = response?.data?.default_virtual_repo_id ?? null;
                setVirtualRepos(list);
                setSelectedVirtualRepoId(defaultId);
            } catch {
                setVirtualRepos([]);
                setSelectedVirtualRepoId(null);
            }
        };
        fetchVirtualRepos();
    }, [repo_to_chat?.repoid, batchType]);

    // ── Fetch templates (Extract only) ────────────────────────────────────
    useEffect(() => {
        if (batchType !== BatchType.EXTRACT || !repo_to_chat?.email) return;
        const fetchTemplates = async () => {
            try {
                const url = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/gettemplate`
                );
                const response = await axios.get(url, { params: { id: repo_to_chat.email } });
                const list = (response.data || []).filter((t) => t.filename);
                setTemplates(list);
                if (list.length > 0) setSelectedTemplate(list[0].filename);
            } catch {
                setTemplates([]);
            }
        };
        fetchTemplates();
    }, [repo_to_chat?.email, batchType]);

    const getTruncatedLabel = (name) => {
        if (!name) return '';
        return name.length > 14 ? name.slice(0, 14) + '…' : name;
    };

    const virtualRepoDropdownItems = [
        {
            label: 'DEFAULT',
            selected: selectedVirtualRepoId == null,
            action: () => setSelectedVirtualRepoId(null),
        },
        ...virtualRepos.map((item) => ({
            label: item.virtual_repo_name || `Config ${item.virtual_repo_id}`,
            selected: selectedVirtualRepoId === item.virtual_repo_id,
            action: () => setSelectedVirtualRepoId(item.virtual_repo_id),
        })),
    ];

    const selectedVirtualRepoLabel = getTruncatedLabel(
        selectedVirtualRepoId == null
            ? 'DEFAULT'
            : virtualRepos.find((v) => v.virtual_repo_id === selectedVirtualRepoId)
                  ?.virtual_repo_name || `Config ${selectedVirtualRepoId}`
    );

    const templateDropdownItems = templates.map((t) => ({
        label: t.filename,
        selected: selectedTemplate === t.filename,
        action: () => setSelectedTemplate(t.filename),
    }));

    // ── File validation ────────────────────────────────────────────────────
    const validateFile = (file) => {
        if (!file) return;
        if (batchType === BatchType.EXTRACT && evalMode) {
            if (!file.name.match(/\.(xlsx|json)$/i)) {
                setError('Only XLSX or JSON files are allowed.');
                return;
            }
        } else if (batchType === BatchType.QNA) {
            if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                setError('Only CSV files are allowed.');
                return;
            }
        }
        setError('');
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setAppendTemplateConflict('');
        validateFile(file);
    };

    const handleFileDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        setSelectedFile(file);
        setAppendTemplateConflict('');
        validateFile(file);
    };

    const handleDragOver = (event) => event.preventDefault();

    // ── Submit ─────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        try {
            if (!repo_to_chat?.repoid || !repo_to_chat?.email) {
                showAlert(AlertType.ERROR, 'Repository information is missing. Please reload the page.');
                return;
            }

            if (batchType === BatchType.EXTRACT) {
                if (!selectedTemplate) {
                    showAlert(AlertType.ERROR, 'Please select a template.');
                    return;
                }
                if (evalMode) {
                    if (!selectedFile) {
                        showAlert(AlertType.ERROR, 'Please upload a benchmark file.');
                        return;
                    }
                    if (error || appendTemplateConflict) return;
                    if (selectedModels.length === 0) {
                        showAlert(AlertType.ERROR, 'Please select at least one model.');
                        return;
                    }
                }
            } else {
                // QnA
                if (!selectedFile) return;
                if (error) return;
                if (evalMode && selectedVirtualRepoId === null && selectedModels.length === 0) {
                    showAlert(AlertType.ERROR, 'Please select at least one model.');
                    return;
                }
            }

            setIsLoading(true);
            const formData = new FormData();
            formData.append('email', repo_to_chat.email);
            formData.append('repo_id', repo_to_chat.repoid);

            const mode = batchType === BatchType.QNA
                ? (evalMode ? BatchMode.EVAL : BatchMode.BATCH)
                : (evalMode ? BatchMode.EXTRACTION_EVAL : BatchMode.EXTRACTION_BATCH);
            formData.append('mode', mode);

            if (batchType === BatchType.EXTRACT) {
                formData.append('template_name', selectedTemplate);
                if (evalMode) {
                    formData.append('file', selectedFile);
                    formData.append('models', JSON.stringify(selectedModels));
                }
            } else {
                // QnA
                formData.append('file', selectedFile);
                if (evalMode) {
                    const modelsToSend = selectedVirtualRepoId !== null ? [] : selectedModels;
                    formData.append('models', JSON.stringify(modelsToSend));
                }
                if (selectedVirtualRepoId != null) {
                    formData.append('virtual_repo_id', selectedVirtualRepoId);
                }
            }

            const response = await axios.post(
                getUrl(`http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/process-batch-eval`),
                formData
            );

            if (response.status >= 200 && response.status < 300) {
                const saved_filename = response.data.filename;
                const ext = response.data.ext || 'csv';
                const anchor = document.createElement('a');
                anchor.href = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/download2?filename=${saved_filename}`
                );
                anchor.download = `${saved_filename}.${ext}`;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            } else {
                showAlert(AlertType.ERROR, 'Could not process your request. Please try again later.');
            }
        } catch (err) {
            const raw = err?.response?.data?.detail;
            const message = Array.isArray(raw)
                ? raw.map((e) => e.msg || JSON.stringify(e)).join('; ')
                : (raw || 'Could not process your request. Please try again later.');
            showAlert(AlertType.ERROR, message);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Submit button disabled logic ───────────────────────────────────────
    const isSubmitDisabled = batchType === BatchType.EXTRACT
        ? (!selectedTemplate || (evalMode && (!selectedFile || !!error || !!appendTemplateConflict || selectedModels.length === 0)))
        : (!!error || !selectedFile);


    // ── File input section (shared between QnA both modes and Extract eval) ─
    const FileInputSection = ({ accept, helperText, subText }) => (
        <div className='flex flex-col w-full'>
            <div className='text-sm font-medium text-texts-secondary300 mb-1'>
                Upload a file
            </div>
            <p className='text-xs text-texts-secondary300 mb-3'>{helperText}</p>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <input
                    type='file'
                    accept={accept}
                    className='bg-backgrounds-white w-full p-2 rounded-xl text-texts-secondary300 border cursor-pointer'
                    onChange={handleFileSelect}
                />
                {!isMobile && (
                    <DragDropFile
                        text={"Drag and drop a file here or click 'Choose Files'"}
                        subText={subText}
                        onDrop={handleFileDrop}
                        onDragOver={handleDragOver}
                    />
                )}
                <div className='pt-3'>
                    <div className='border rounded-xl bg-backgrounds-white'>
                        <div className='text-texts-secondary300 p-3 text-sm flex items-center gap-2'>
                            <FontAwesomeIcon icon={faFile} className='text-texts-primary100' />
                            {selectedFile ? selectedFile.name : 'No file selected'}
                        </div>
                    </div>
                    {error && (
                        <div className='flex items-center text-buttons-alert400 mt-2 text-sm gap-1'>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            {error}
                        </div>
                    )}
                    {appendTemplateConflict && (
                        <div className='flex items-center text-buttons-alert400 mt-2 text-sm gap-1'>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            {appendTemplateConflict}
                        </div>
                    )}
                </div>
            </form>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div ref={containerRef} className='relative w-full flex flex-col p-4'>
            {isLoading && <NoInteractionView viewText={'Waiting for the file to download ...'} />}

            {/* Description */}
            <p className='text-sm text-texts-secondary300 mb-4'>
                {batchType === BatchType.QNA
                    ? 'Run questions against this repository, with optional model evaluation against responses.'
                    : 'Run extraction on all repository files using a template with optional model evaluation against responses.'
                }
            </p>

            {/* Mode selection */}
            <div className='mb-4 flex items-center gap-2'>
                <span className='text-sm font-medium text-texts-secondary300'>Mode:</span>
                <Dropdown
                    dropDownList={[
                        { label: 'Batch', selected: !evalMode, action: () => setEvalMode(false) },
                        { label: 'Batch & Eval', selected: evalMode, action: () => setEvalMode(true) },
                    ]}
                    showButtonText={true}
                    buttonText={evalMode ? 'Batch & Eval' : 'Batch'}
                    placement='bottomLeft'
                    getPopupContainer={() => document.body}
                />
            </div>

            {/* ── QnA view ───────────────────────────────────────────────── */}
            {batchType === BatchType.QNA && (
                <>
                    {/* Virtual repo dropdown */}
                    <div className='mb-4 flex items-center gap-2'>
                        <span className='text-sm text-texts-secondary300'>Repo:</span>
                        <Dropdown
                            dropDownList={virtualRepoDropdownItems}
                            showButtonText={true}
                            buttonText={selectedVirtualRepoLabel}
                            buttonHoverText='Virtual repository configuration'
                            buttonClassName='px-3 h-9 flex items-center justify-center border border-backgrounds-border rounded-lg text-sm text-texts-secondary300 truncate max-w-[10rem]'
                            placement='bottomLeft'
                            getPopupContainer={() => document.body}
                        />
                    </div>

                    {/* Model display */}
                    <div className='mb-4'>
                        {!evalMode ? (
                            <div>
                                <div className='text-sm font-medium text-texts-secondary300 mb-1'>Model</div>
                                <p className='text-xs text-texts-secondary300'>
                                    {selectedVirtualRepoId != null
                                        ? 'Model is determined by the selected repo configuration.'
                                        : 'Using model from preferences.'}
                                </p>
                            </div>
                        ) : selectedVirtualRepoId !== null ? (
                            <div>
                                <div className='text-sm font-medium text-texts-secondary300 mb-1'>Select models</div>
                                <p className='text-xs text-texts-secondary300'>Model is determined by the selected repo configuration.</p>
                            </div>
                        ) : (
                            <ModelMultiSelect
                                selectedModels={selectedModels}
                                onChange={setSelectedModels}
                                availableCustomModels={availableCustomModels}
                            />
                        )}
                    </div>

                    {/* File input — same field for both Batch and Eval */}
                    <FileInputSection
                        accept='.csv'
                        helperText={
                            evalMode
                                ? 'Upload a CSV file with questions and answers from Batch, or re-upload a previous Batch eval output to compare additional models.'
                                : 'Upload a CSV file with questions in the first column'
                        }
                    />
                </>
            )}

            {/* ── Extract view ────────────────────────────────────────────── */}
            {batchType === BatchType.EXTRACT && (
                <>
                    {/* Template dropdown */}
                    <div className='mb-4 flex items-center gap-2'>
                        <span className='text-sm text-texts-secondary300 whitespace-nowrap'>Template:</span>
                        {templates.length === 0 ? (
                            <span className='text-sm text-texts-secondary300'>No templates available.</span>
                        ) : (
                            <Dropdown
                                dropDownList={templateDropdownItems}
                                showButtonText={true}
                                buttonText={selectedTemplate || 'Select a template…'}
                                buttonHoverText='Select extraction template'
                                placement='bottomLeft'
                                getPopupContainer={() => document.body}
                                listClassName='!max-h-[30vh]'
                            />
                        )}
                    </div>

                    {/* Model display */}
                    <div className='mb-4'>
                        {!evalMode ? (
                            <div>
                                <div className='text-sm font-medium text-texts-secondary300 mb-1'>Model</div>
                                <p className='text-xs text-texts-secondary300'>Using model from preferences.</p>
                            </div>
                        ) : (
                            <ModelMultiSelect
                                selectedModels={selectedModels}
                                onChange={setSelectedModels}
                                availableCustomModels={availableCustomModels}
                            />
                        )}
                    </div>

                    {/* File input — Eval only */}
                    {evalMode ? (
                        <FileInputSection
                            accept='.xlsx,.json'
                            helperText='Upload the XLSX from Extraction Batch, or re-upload a previous Extraction Eval output to compare additional models.'
                        />
                    ) : (
                        <div className='rounded-xl border bg-backgrounds-white p-4 min-h-[300px] flex flex-col justify-between'>
                            <div>
                                <p className='text-sm font-medium text-texts-secondary300 mb-5'>What this will do</p>
                                <div className='flex flex-col gap-4'>
                                    <div className='flex items-start gap-3 text-xs text-texts-secondary300'>
                                        <span className='mt-1 w-1.5 h-1.5 rounded-full bg-backgrounds-primary200 flex-shrink-0' />
                                        Runs extraction on every file in this repository using the selected template
                                    </div>
                                    <div className='flex items-start gap-3 text-xs text-texts-secondary300'>
                                        <span className='mt-1 w-1.5 h-1.5 rounded-full bg-backgrounds-primary200 flex-shrink-0' />
                                        Model is resolved automatically from your model preferences
                                    </div>
                                    <div className='flex items-start gap-3 text-xs text-texts-secondary300'>
                                        <span className='mt-1 w-1.5 h-1.5 rounded-full bg-backgrounds-primary200 flex-shrink-0' />
                                        Output XLSX can be uploaded to Batch & Eval to score extraction quality
                                    </div>
                                </div>
                            </div>
                            <p className='text-xs text-texts-secondary300 opacity-50'>
                                Switch to Batch & Eval mode to upload the answers and score the results on different models.
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* Footer buttons */}
            <div className='flex w-full items-center justify-end mt-6'>
                <Button
                    text={batchType === BatchType.EXTRACT ? 'Run & Download XLSX' : 'Run & Download CSV'}
                    title={batchType === BatchType.EXTRACT ? 'Run extraction and download results' : 'Run and download results'}
                    className='w-1/3'
                    onClick={handleSubmit}
                    icon={faFileUpload}
                    type={'success'}
                    disabled={isSubmitDisabled}
                />
            </div>
        </div>
    );
};

export default BatchAndEval;
